import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Truck, Plus, X, Printer, PencilSimple, PaperPlaneTilt, Warning,
} from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";
import SerialEditor from "@/components/SerialEditor";
import { fmtDate } from "@/lib/dates";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blankPart = () => ({
  name: "", quantity: 1, stock_code: null, group: null, uom: null, notes: "",
  serials: [], weight_kg: "", length_cm: "", width_cm: "", height_cm: "",
});

const STATUS_BADGE = {
  draft:     "bg-neutral-200 text-neutral-800",
  approved:  "bg-amber-100 text-amber-900",
  released:  "bg-emerald-700 text-white",
  cancelled: "bg-red-100 text-red-800 line-through",
};

export default function DeliveryOrdersPage() {
  const { user } = useAuth();
  const canCreate  = ["manager", "supervisor"].includes(user.role);
  const canApprove = user.role === "manager";
  const canRelease = ["storekeeper", "manager"].includes(user.role);
  const canCancel  = user.role === "manager";
  const canEmail   = ["manager", "supervisor"].includes(user.role);
  const isStorekeeper = user.role === "storekeeper";

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (search.trim()) params.q = search.trim();
      const [d, c, r] = await Promise.all([
        api.get("/delivery-orders", { params }),
        api.get("/customers", { params: { active_only: true } }),
        api.get("/regions"),
      ]);
      setRows(d.data || []);
      setCustomers(c.data || []);
      setRegions(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [search]);

  const openPdf = (d) => window.open(`${API_BASE}/delivery-orders/${d.id}/receipt.pdf`, "_blank", "noopener");

  const approve = async (d) => {
    if (!window.confirm(`Approve ${d.do_no}? It will become visible to the Storekeeper for release.`)) return;
    try {
      await api.post(`/delivery-orders/${d.id}/approve`);
      toast.success(`${d.do_no} approved — Storekeeper can now release`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const release = async (d) => {
    if (!window.confirm(`Release ${d.do_no} to ${d.customer_name}? Main stock will be deducted.`)) return;
    try {
      await api.post(`/delivery-orders/${d.id}/release`);
      toast.success(`${d.do_no} released — stock deducted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancel = async (d) => {
    const reason = window.prompt(`Cancel ${d.do_no}? (Optional reason)`, "");
    if (reason === null) return;
    try {
      await api.post(`/delivery-orders/${d.id}/cancel`, null, { params: reason ? { reason } : {} });
      toast.success(`${d.do_no} cancelled`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const emailDoc = async (d) => {
    if (!window.confirm(`Email ${d.do_no} to ${d.customer_email || "customer"}? (You will be CC'd)`)) return;
    try {
      const res = await api.post(`/delivery-orders/${d.id}/email`);
      toast.success(`Sent to ${res.data.to}${res.data.cc?.length ? ` · CC ${res.data.cc.join(", ")}` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="border-b border-neutral-200 pt-10">
        <div className="flex items-start justify-between px-10 pb-6">
          <div>
            <div className="label-eyebrow">Outbound</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2 flex items-center gap-3">
              <Truck size={36} weight="duotone" className="text-emerald-700" /> Delivery Orders
            </h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-lg">
              {isStorekeeper
                ? "Delivery Orders APPROVED by a Manager and awaiting release. Click Release to deduct stock and complete the shipment."
                : <>Numbered <span className="font-mono">DO-10001+</span>. Manager/Supervisor drafts → Manager approves → Storekeeper (or Manager) releases → stock deducts on release. Cancellation on a released DO refunds stock.</>}
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreate(true)} data-testid="new-do-btn"
              className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
              <Plus size={14} weight="bold" className="mr-1" /> New DO
            </Button>
          )}
        </div>
        <div className="px-10 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Search DO no or customer…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="h-10 rounded-none border-neutral-300" data-testid="do-search" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="do-status-filter" className="h-10 rounded-none border-neutral-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="released">Released</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-10 py-6">
        {loading ? (
          <div className="text-sm text-neutral-500 font-mono">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-8 text-center">
            No delivery orders yet.
          </div>
        ) : (
          <div className="border border-neutral-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-300">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">DO No</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Customer</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Region</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Lines</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Status</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Created</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="border-b border-neutral-100 last:border-0" data-testid={`do-row-${d.do_no}`}>
                    <td className="px-3 py-2 font-mono font-semibold">
                      {d.do_no}
                      {d.from_quotation_no && (
                        <div className="mt-0.5">
                          <span className="text-[9px] font-mono uppercase tracking-wider bg-purple-100 text-purple-800 px-1 py-0.5"
                            title={`Auto-generated from ${d.from_quotation_no} · ${d.from_invoice_iv_no || ""}`}>
                            ← {d.from_quotation_no}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">{d.customer_name}<div className="text-[10px] text-neutral-500">{d.customer_address}</div></td>
                    <td className="px-3 py-2 text-xs">{d.region || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{d.parts?.length || 0}</td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[d.status] || ""}`}>{d.status}</span></td>
                    <td className="px-3 py-2 text-[11px] font-mono">{fmtDate(d.created_at)}<div className="text-[10px] text-neutral-500">{d.created_by_name}</div></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openPdf(d)} title="Print / PDF" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-neutral-900" data-testid={`do-print-${d.do_no}`}><Printer size={13} weight="bold" /></button>
                        {canEmail && d.customer_email && (
                          <button onClick={() => emailDoc(d)} title={`Email to ${d.customer_email}`} className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-emerald-700 hover:text-emerald-800" data-testid={`do-email-${d.do_no}`}><PaperPlaneTilt size={13} weight="bold" /></button>
                        )}
                        {canCreate && d.status === "draft" && (
                          <button onClick={() => setEditing(d)} title="Edit" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-neutral-900" data-testid={`do-edit-${d.do_no}`}><PencilSimple size={13} weight="bold" /></button>
                        )}
                        {canApprove && d.status === "draft" && (
                          <button onClick={() => approve(d)} className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-amber-700 text-amber-900 hover:bg-amber-700 hover:text-white" data-testid={`do-approve-${d.do_no}`}>Approve</button>
                        )}
                        {canRelease && d.status === "approved" && (
                          <button onClick={() => release(d)} className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white" data-testid={`do-release-${d.do_no}`}>Release</button>
                        )}
                        {canCancel && !["released", "cancelled"].includes(d.status) && (
                          <button onClick={() => cancel(d)} title="Cancel" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600" data-testid={`do-cancel-${d.do_no}`}><X size={13} weight="bold" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showCreate || editing) && (
        <DOEditDialog
          editing={editing}
          customers={customers}
          regions={regions}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DOEditDialog({ editing, customers, regions, onClose, onSaved }) {
  const isEdit = !!editing;
  const [customerId, setCustomerId] = useState(editing?.customer_id || "");
  const [region, setRegion] = useState(editing?.region || "");
  const [items, setItems] = useState(() =>
    (editing?.parts?.length ? editing.parts : [blankPart()]).map((p) => ({
      name: p.name || "", quantity: p.quantity || 1,
      stock_code: p.stock_code || null, group: p.group || null, uom: p.uom || null,
      notes: p.notes || "", serials: Array.isArray(p.serials) ? p.serials : [],
      weight_kg: p.weight_kg ?? "", length_cm: p.length_cm ?? "",
      width_cm: p.width_cm ?? "", height_cm: p.height_cm ?? "",
    })),
  );
  const [notes, setNotes] = useState(editing?.notes || "");
  const [busy, setBusy] = useState(false);
  const [availability, setAvailability] = useState({});

  const setItem = (i, key, val) => {
    const next = items.slice(); next[i] = { ...next[i], [key]: val }; setItems(next);
  };
  const pickItem = (i, picked) => setItem(i, "_row", null) || setItems(items.map((row, idx) => (idx === i ? {
    ...row, name: picked.name || "", stock_code: picked.stock_code || null,
    group: picked.group || null, uom: picked.uom || null,
  } : row)));
  const addRow = () => setItems([...items, blankPart()]);
  const removeRow = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  // Availability check for stock lines
  useEffect(() => {
    const codes = [...new Set(items.map((r) => r.stock_code).filter(Boolean))];
    if (!codes.length) { setAvailability({}); return; }
    (async () => {
      try {
        const res = await api.post("/stock/availability", { stock_codes: codes });
        setAvailability(res.data.availability || {});
      } catch { /* silent */ }
    })();
  }, [items.map((r) => r.stock_code).join("|")]);

  const submit = async () => {
    if (!customerId) return toast.error("Pick a customer");
    const clean = items.filter((p) => (p.name || "").trim() && Number(p.quantity) > 0);
    if (!clean.length) return toast.error("Add at least one line");
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        region: region || null,
        notes,
        parts: clean.map((p) => ({
          name: p.name.trim(),
          quantity: Math.max(1, Number(p.quantity) || 1),
          stock_code: p.stock_code || null,
          group: p.group || null, uom: p.uom || null, notes: p.notes || "",
          serials: (p.serials || []).map((s) => String(s).trim()).filter(Boolean),
          weight_kg: p.weight_kg === "" ? null : Number(p.weight_kg),
          length_cm: p.length_cm === "" ? null : Number(p.length_cm),
          width_cm:  p.width_cm  === "" ? null : Number(p.width_cm),
          height_cm: p.height_cm === "" ? null : Number(p.height_cm),
        })),
      };
      if (isEdit) await api.patch(`/delivery-orders/${editing.id}`, payload);
      else await api.post("/delivery-orders", payload);
      toast.success(isEdit ? "DO updated" : "DO created (draft)");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[92vh] overflow-y-auto" data-testid="do-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? `Edit ${editing.do_no}` : "New Delivery Order"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Customer *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="do-customer" className="h-10 rounded-none border-neutral-400"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        {c.name}
                        {c.customer_type === "walkin" && (
                          <span className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900 px-1 py-0.5">
                            walk-in
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="do-region" className="h-10 rounded-none border-neutral-400"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">—</SelectItem>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="label-eyebrow">Lines *</Label>
              <Button onClick={addRow} size="sm" variant="outline" className="h-7 rounded-none border-neutral-400" data-testid="do-add-line">
                <Plus size={12} weight="bold" className="mr-1" /> Add line
              </Button>
            </div>
            {items.map((p, i) => {
              const onHand = p.stock_code ? availability[p.stock_code] : null;
              const insufficient = onHand != null && Number(p.quantity) > onHand;
              return (
                <div key={i} className="border border-neutral-200 p-2 space-y-2" data-testid={`do-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-8"><StockPartCombobox value={p} onChange={(pk) => pickItem(i, pk)} testid={`do-picker-${i}`} /></div>
                    <Input type="number" min={1} value={p.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)}
                      className={`col-span-3 h-9 rounded-none border-neutral-400 text-right font-mono ${insufficient ? "border-red-500" : ""}`} data-testid={`do-qty-${i}`} />
                    <button type="button" onClick={() => removeRow(i)} disabled={items.length === 1}
                      className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600 disabled:opacity-40" aria-label="Remove"><X size={13} weight="bold" /></button>
                  </div>
                  {insufficient && (
                    <div className="text-[10px] text-red-700 flex items-center gap-1"><Warning size={10} weight="bold" /> Insufficient on-hand ({onHand} available)</div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    <Input placeholder="Wt kg" type="number" step="0.01" value={p.weight_kg}
                      onChange={(e) => setItem(i, "weight_kg", e.target.value)} className="h-8 rounded-none border-neutral-300 text-xs" data-testid={`do-wt-${i}`} />
                    <Input placeholder="L cm" type="number" step="0.01" value={p.length_cm}
                      onChange={(e) => setItem(i, "length_cm", e.target.value)} className="h-8 rounded-none border-neutral-300 text-xs" data-testid={`do-L-${i}`} />
                    <Input placeholder="W cm" type="number" step="0.01" value={p.width_cm}
                      onChange={(e) => setItem(i, "width_cm", e.target.value)} className="h-8 rounded-none border-neutral-300 text-xs" data-testid={`do-W-${i}`} />
                    <Input placeholder="H cm" type="number" step="0.01" value={p.height_cm}
                      onChange={(e) => setItem(i, "height_cm", e.target.value)} className="h-8 rounded-none border-neutral-300 text-xs" data-testid={`do-H-${i}`} />
                  </div>
                  <SerialEditor serials={p.serials || []} onChange={(next) => setItem(i, "serials", next)} testidPrefix={`do-serial-${i}`} />
                </div>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full rounded-none border border-neutral-400 px-2 py-1.5 text-sm focus:outline-none focus:border-neutral-900" data-testid="do-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="do-save" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create draft")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
